/**
 * TanStack Query hooks for the chatbot templates & FAQ editor.
 *
 * Query keys live in `store-settings`'s `settingsKeys` registry (imported, not duplicated) — the
 * established cross-feature pattern in this app (see `features/content`, which does the same for
 * its `SettingsScaffold`/`FeedbackSnackbar`/etc.). `chatbot-settings` never imports from
 * `store-settings`'s page/route modules, only from its shared-like public barrel, so there is no
 * import cycle back the other way (`store-settings`'s own interim chatbot card is deliberately
 * self-contained instead — see `TelegramSettingsPage`'s doc comment).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import { SETTINGS_ROOT_KEY, settingsKeys } from '@/features/store-settings';

import {
  acceptChatbotTemplateDefault,
  getChatbotTemplate,
  getChatbotTemplateCatalog,
  putChatbotTemplate,
  resetChatbotTemplate,
} from '../api/chatbotTemplatesApi';
import type { TemplateScope } from '../api/chatbotTemplatesApi';
import {
  deleteChatbotFaqIntent,
  listChatbotFaqIntents,
  putChatbotFaqIntent,
} from '../api/chatbotFaqApi';
import { listContentLanguages } from '../api/contentLanguagesApi';
import {
  deleteChatbotChannelSettings,
  getChatbotAvailability,
  getChatbotChannelSettings,
  getChatbotDictionaries,
  getChatbotSettings,
  getEffectiveChatbotSettings,
  putChatbotChannelSettings,
  updateChatbotSettings,
} from '../api/chatbotSettingsApi';
import type {
  ChatbotAvailability,
  ChatbotChannel,
  ChatbotChannelSettings,
  ChatbotDictionaries,
  ChatbotFaqIntent,
  ChatbotFaqIntentList,
  ChatbotGeneralSettings,
  ChatbotTemplate,
  ChatbotTemplateCatalog,
  ChatbotTemplateList,
  ContentLanguage,
  EffectiveChatbotSettings,
  UpdateChatbotChannelSettingsRequest,
  UpdateChatbotGeneralSettingsRequest,
  UpsertChatbotFaqIntentRequest,
  UpsertChatbotTemplateRequest,
} from '../model/types';

/** The templates prefix both the legacy `chatbotTemplates(locale)` and
 * `chatbotTemplate(key,locale,channel)` item keys share (still used by the editor drawer's
 * single-key reads). The catalogue itself lives under its own `chatbotTemplateCatalog` prefix. */
const TEMPLATES_PREFIX = [SETTINGS_ROOT_KEY, 'chatbot', 'templates'] as const;
const TEMPLATE_CATALOG_PREFIX = [SETTINGS_ROOT_KEY, 'chatbot', 'templateCatalog'] as const;

// ── Content languages (A3) ───────────────────────────────────────────────────────
export function useContentLanguages() {
  return useQuery<ContentLanguage[], NormalizedApiError>({
    queryKey: settingsKeys.chatbotLanguages,
    queryFn: listContentLanguages,
    staleTime: 60_000,
  });
}

// ── Dictionaries (contract delta 2026-09-23) ──────────────────────────────────────
/** Identical for every tenant of a contour — cached for the session, evicted only on tenant switch
 * (`settingsKeys.all`). No static fallback on error (design §17.3.0). */
export function useChatbotDictionaries() {
  return useQuery<ChatbotDictionaries, NormalizedApiError>({
    queryKey: settingsKeys.chatbotDictionaries,
    queryFn: getChatbotDictionaries,
    staleTime: Infinity,
  });
}

// ── Templates catalogue (contract delta 2026-09-23 — replaces the deleted
//    `useChatbotTemplatesCatalog`'s per-key `useQueries` composition) ─────────────
export function useChatbotTemplateCatalog(locale: string | undefined, channel?: ChatbotChannel) {
  return useQuery<ChatbotTemplateCatalog, NormalizedApiError>({
    queryKey: settingsKeys.chatbotTemplateCatalog(locale ?? '', channel),
    queryFn: () => getChatbotTemplateCatalog(locale ?? '', channel),
    enabled: Boolean(locale),
  });
}

function useInvalidateTemplateCatalogues() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: TEMPLATE_CATALOG_PREFIX });
}

// ── One row (editor) ─────────────────────────────────────────────────────────────
export function useChatbotTemplate(key: string, scope: TemplateScope, enabled = true) {
  return useQuery<ChatbotTemplate, NormalizedApiError>({
    queryKey: settingsKeys.chatbotTemplate(key, scope.locale, scope.channel),
    queryFn: () => getChatbotTemplate(key, scope),
    enabled: enabled && Boolean(scope.locale),
  });
}

function useInvalidateTemplateRow() {
  const qc = useQueryClient();
  return (key: string, scope: TemplateScope) =>
    qc.invalidateQueries({
      queryKey: settingsKeys.chatbotTemplate(key, scope.locale, scope.channel),
    });
}

/**
 * The invalidation matrix of templates doc §13.1: a template write invalidates its own row, every
 * open catalogue (any locale/channel — the write may change `referencedByIntents`/`issues` on other
 * scopes too) and the chatbot page's required-texts card (`chatbotAvailability`, settings doc §17.4).
 */
export function usePutChatbotTemplate() {
  const invalidateRow = useInvalidateTemplateRow();
  const invalidateCatalogues = useInvalidateTemplateCatalogues();
  const qc = useQueryClient();
  return useMutation<
    ChatbotTemplate,
    NormalizedApiError,
    { key: string; scope: TemplateScope; body: UpsertChatbotTemplateRequest }
  >({
    mutationFn: ({ key, scope, body }) => putChatbotTemplate(key, scope, body),
    onSuccess: (_data, { key, scope }) => {
      void invalidateRow(key, scope);
      void invalidateCatalogues();
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotAvailability });
    },
  });
}

export function useResetChatbotTemplate() {
  const invalidateRow = useInvalidateTemplateRow();
  const invalidateCatalogues = useInvalidateTemplateCatalogues();
  const qc = useQueryClient();
  return useMutation<
    void,
    NormalizedApiError,
    { key: string; scope: TemplateScope; cascade?: boolean }
  >({
    mutationFn: ({ key, scope, cascade }) => resetChatbotTemplate(key, scope, cascade),
    onSuccess: (_data, { key, scope }) => {
      void invalidateRow(key, scope);
      void invalidateCatalogues();
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotAvailability });
    },
  });
}

export function useAcceptChatbotTemplateDefault() {
  const qc = useQueryClient();
  const invalidateCatalogues = useInvalidateTemplateCatalogues();
  return useMutation<ChatbotTemplateList, NormalizedApiError, string>({
    mutationFn: (key) => acceptChatbotTemplateDefault(key),
    // Accepting a default affects every locale that has one — invalidate the whole templates
    // prefix (both the legacy item rows and every open catalogue), not just the selected locale.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATES_PREFIX });
      void invalidateCatalogues();
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotAvailability });
    },
  });
}

// ── Chatbot behaviour: general row, per-channel overrides, effective view, availability ─────────
//
// Backs `ChatbotSettingsPage` (design-chatbot-settings-page.md §3, §7, §11). Query keys stay in
// `store-settings`'s `settingsKeys` registry (the established cross-feature pattern this module
// already follows for templates/FAQ above) — `chatbotGeneral`/`chatbotAvailability` are the same
// keys the retired interim bridge card used, so a tenant switch's `settingsKeys.all` eviction still
// covers them without any change there.

export function useChatbotSettings() {
  return useQuery<ChatbotGeneralSettings, NormalizedApiError>({
    queryKey: settingsKeys.chatbotGeneral,
    queryFn: getChatbotSettings,
  });
}

export function useUpdateChatbotSettings() {
  const qc = useQueryClient();
  return useMutation<
    ChatbotGeneralSettings,
    NormalizedApiError,
    UpdateChatbotGeneralSettingsRequest
  >({
    mutationFn: updateChatbotSettings,
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.chatbotGeneral, data);
      // The general row feeds every channel's `?? general` resolution — invalidate both channels'
      // effective views (never the channel overrides themselves, which are unaffected).
      void qc.invalidateQueries({ queryKey: [SETTINGS_ROOT_KEY, 'chatbot', 'effective'] });
    },
  });
}

/**
 * `null` = confirmed no override for this channel (`GET` 404 — "inherits general", not an error);
 * `undefined` = still loading. Never throws for a 404; any other failure propagates as a normal
 * query error.
 */
export function useChatbotChannelSettings(channel: ChatbotChannel) {
  return useQuery<ChatbotChannelSettings | null, NormalizedApiError>({
    queryKey: settingsKeys.chatbotChannel(channel),
    queryFn: async () => {
      try {
        return await getChatbotChannelSettings(channel);
      } catch (error) {
        if ((error as NormalizedApiError).status === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useUpdateChatbotChannelSettings(channel: ChatbotChannel) {
  const qc = useQueryClient();
  return useMutation<
    ChatbotChannelSettings,
    NormalizedApiError,
    UpdateChatbotChannelSettingsRequest
  >({
    mutationFn: (body) => putChatbotChannelSettings(channel, body),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.chatbotChannel(channel), data);
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotGeneral });
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotEffective(channel) });
    },
  });
}

/** "Reset to general" — 204, so the cache is written to `null` directly rather than refetched. */
export function useDeleteChatbotChannelSettings(channel: ChatbotChannel) {
  const qc = useQueryClient();
  return useMutation<void, NormalizedApiError, void>({
    mutationFn: () => deleteChatbotChannelSettings(channel),
    onSuccess: () => {
      qc.setQueryData(settingsKeys.chatbotChannel(channel), null);
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotGeneral });
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotEffective(channel) });
    },
  });
}

/** Omit `channel` for the general resolution. */
export function useEffectiveChatbotSettings(channel?: ChatbotChannel) {
  return useQuery<EffectiveChatbotSettings, NormalizedApiError>({
    queryKey: settingsKeys.chatbotEffective(channel),
    queryFn: () => getEffectiveChatbotSettings(channel),
  });
}

export function useChatbotAvailability() {
  return useQuery<ChatbotAvailability, NormalizedApiError>({
    queryKey: settingsKeys.chatbotAvailability,
    queryFn: getChatbotAvailability,
  });
}

// ── FAQ ───────────────────────────────────────────────────────────────────────────
export function useChatbotFaqIntents(locale: string | undefined) {
  return useQuery<ChatbotFaqIntentList, NormalizedApiError>({
    queryKey: settingsKeys.chatbotFaq(locale ?? ''),
    queryFn: () => listChatbotFaqIntents(locale ?? ''),
    enabled: Boolean(locale),
  });
}

export function usePutChatbotFaqIntent(locale: string) {
  const qc = useQueryClient();
  const invalidateCatalogues = useInvalidateTemplateCatalogues();
  return useMutation<
    ChatbotFaqIntent,
    NormalizedApiError,
    { intentCode: string; body: UpsertChatbotFaqIntentRequest }
  >({
    mutationFn: ({ intentCode, body }) => putChatbotFaqIntent(intentCode, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotFaq(locale) });
      // `referencedByIntents`/`ANSWER_TEMPLATE_MISSING`/`USED_AS_FAQ_ANSWER` all live on the
      // catalogue's items, not the FAQ list itself (templates doc §13.1).
      void invalidateCatalogues();
    },
  });
}

export function useDeleteChatbotFaqIntent(locale: string) {
  const qc = useQueryClient();
  const invalidateCatalogues = useInvalidateTemplateCatalogues();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: (intentCode) => deleteChatbotFaqIntent(intentCode, locale),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.chatbotFaq(locale) });
      void invalidateCatalogues();
    },
  });
}
